/**
 * @file API Service
 * @description A dedicated module for handling all API communications.
 * It abstracts the logic of making fetch requests, handling WordPress AJAX,
 * and processing responses.
 */

/**
 * A wrapper for the native fetch API for WordPress admin-ajax.php requests.
 * Implements retry logic and timeouts for better reliability.
 *
 * @param {string} action - The WordPress AJAX action hook.
 * @param {object} [data={}] - The data to send with the request.
 * @param {object} [options={}] - Optional settings like timeout and retries.
 * @returns {Promise<any>} The response data, parsed as JSON if possible, otherwise as text.
 */
async function makeAjaxRequest(action, data = {}, options = {}) {
  const defaultOptions = {
    timeout: 15000, // Increased timeout
    retries: 2,
  };

  const config = { ...defaultOptions, ...options };

  // Add nonce for write operations
  const writeOperations = [
    "add_to_collection",
    "remove_from_collection",
    "clear_collection",
    "fetch_customer_detail",
  ];
  const isWriteOperation = writeOperations.includes(action);

  const params = new URLSearchParams({
    action,
    ...data,
  });

  // Add system nonce for write operations
  if (isWriteOperation && typeof fdiAjaxData !== "undefined" && fdiAjaxData.nonce) {
    params.append("nonce", fdiAjaxData.nonce);
  }

  for (let attempt = 0; attempt <= config.retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch("/wp-admin/admin-ajax.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      if (attempt === config.retries) {
        console.error(
          `AJAX request failed after ${config.retries} retries for action: ${action}`,
          error,
        );
        throw error;
      }
      // Wait before retry with exponential backoff
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
}

/**
 * A wrapper for fetching data from the WordPress REST API, automatically parsing pagination headers.
 *
 * @param {string} url - The full URL for the REST API request.
 * @param {object} [options={}] - Standard fetch API options.
 * @returns {Promise<{data: any, headers: object}>} An object containing the JSON response data and key pagination headers.
 */
async function fetchWithHeaders(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return {
    data: await response.json(),
    headers: {
      total: response.headers.get("X-WP-Total"),
      totalPages: response.headers.get("X-WP-TotalPages"),
      perPage: response.headers.get("X-WP-PerPage"),
    },
  };
}

/**
 * @namespace ApiService
 * @description A singleton service for all backend communication.
 * @property {function} ajax - For making requests to WordPress's admin-ajax.php.
 * @property {function} fetch - For making requests to the WordPress REST API.
 */
export const ApiService = {
  ajax: makeAjaxRequest,
  fetch: fetchWithHeaders,
};
